import type { TrackPoint } from '@/stores/geolocation-store'

export interface ClippedTrack {
  coords: [number, number][]
  currentPos: [number, number] | null
}

const EMPTY: ClippedTrack = { coords: [], currentPos: null }

/**
 * Clip a sorted-by-timestamp track to a given time.
 *
 * Mirrors the Cesium `SampledPositionProperty` behavior used by the legacy
 * viewer: NONE backward extrapolation (hidden before first sample), HOLD
 * forward extrapolation (sticks to last sample after the track ends).
 *
 * `timestampsMs` must be the same length as `track` and contain pre-parsed
 * epoch ms; called per scrubber tick, so re-parsing ISO strings inside the
 * binary search loop would dominate the cost for long tracks.
 */
export function clipTrackToTime(
  track: TrackPoint[],
  timestampsMs: number[],
  timeMs: number
): ClippedTrack {
  if (track.length === 0) return EMPTY

  const firstMs = timestampsMs[0]
  const lastMs = timestampsMs[track.length - 1]
  if (!Number.isFinite(firstMs) || !Number.isFinite(lastMs)) return EMPTY

  if (timeMs < firstMs) return EMPTY

  if (timeMs >= lastMs) {
    const coords = track.map(p => [p.longitude, p.latitude] as [number, number])
    return { coords, currentPos: coords[coords.length - 1] }
  }

  // Binary search: largest index with timestamp <= timeMs.
  let lo = 0
  let hi = track.length - 1
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1
    if (timestampsMs[mid] <= timeMs) lo = mid
    else hi = mid - 1
  }
  const idx = lo
  const t0 = timestampsMs[idx]
  const t1 = timestampsMs[idx + 1]
  const frac = t1 > t0 ? (timeMs - t0) / (t1 - t0) : 0
  const lng = track[idx].longitude + frac * (track[idx + 1].longitude - track[idx].longitude)
  const lat = track[idx].latitude + frac * (track[idx + 1].latitude - track[idx].latitude)

  const coords: [number, number][] = []
  for (let i = 0; i <= idx; i++) {
    coords.push([track[i].longitude, track[i].latitude])
  }
  coords.push([lng, lat])

  return { coords, currentPos: [lng, lat] }
}
